import {
  createRouter, createWebHashHistory, RouteRecordRaw,
} from 'vue-router';
import commonStore from '@/store/common';
import useUserHooks from '@/hooks/user.hooks';
import Login from '../views/Login.vue';
import IsUndefined from '../views/Public/404.vue';
import StudentRouter from './modules/student';

const { getUserInfo } = useUserHooks();

/**
 * router role rule
 * role include ['admin','teacher','student']
 *
 * meta: {
    title: '标题',
    icon: '图标',
    role: ['角色'],
    hidden: true
  },
 */

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'login',
    component: Login,
  },
  {
    path: '/404',
    name: '404',
    component: IsUndefined,
  },
  {
    path: '/Teacher',
    name: 'case',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "Teacher" */ '../views/Teacher/index.vue'),
    children: [
      {
        path: '/Teacher/home',
        name: 'teacher-homepage',
        component: () => import(/* webpackChunkName: "teacher_home" */ '@/views/Teacher/homepage/index.vue'),
        meta: {
          title: '教师首页',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/userManager',
        name: 'Teacher/userManager',
        component: () => import(/* webpackChunkName: "userlist_index" */ '@/views/Teacher/userManager/index.vue'),
        meta: {
          title: '用户管理',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/experimentManager',
        name: 'Teacher/experimentManager',
        component: () => import(/* webpackChunkName: "experiment_index" */ '@/views/Teacher/experimentManager/index.vue'),
        meta: {
          title: '实验管理',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/customCase/:caseId?',
        name: 'Teacher/CustomCase',
        component: () => import(/* webpackChunkName: "customcase_index" */ '@/views/Teacher/customCase/index.vue'),
        meta: {
          title: '自定义案例',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/customCase/edit/:caseId?',
        name: 'Teacher/CustomCaseEdit',
        component: () => import(/* webpackChunkName: "customcase_index" */ '@/views/Teacher/customCaseEdit/index.vue'),
        meta: {
          title: '编辑自定义案例',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/reportManager',
        name: 'Teacher/reportManager',
        component: () => import(/* webpackChunkName: "reportManager_index" */ '@/views/Teacher/reportManager/index.vue'),
        meta: {
          title: '实验报告',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/reportMember/:experimentId',
        name: 'Teacher/reportMember',
        component: () => import(/* webpackChunkName: "reportMember" */ '@/views/Teacher/reportManager/reportMember.vue'),
        meta: {
          title: '报告列表',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/reportInfo/:experimentId/:userId',
        name: 'Teacher/reportInfo',
        component: () => import(/* webpackChunkName: "reportInfo" */ '@/views/Teacher/reportManager/reportInfo.vue'),
        meta: {
          title: '报告详情',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/caseAnalysis',
        name: 'Teacher/CaseAnalysis',
        component: () => import(/* webpackChunkName: "caseAnalysis_index" */ '@/views/Teacher/caseAnalysis/index.vue'),
        meta: {
          title: '案例分析',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/caseAnalysis/jobDetailList/:expId',
        name: 'Teacher/CaseAnalysis/JobDetailList',
        component: () => import(/* webpackChunkName: "JobDetailList" */ '@/views/Teacher/caseAnalysis/jobDetailList.vue'),
        meta: {
          title: '作业查看列表',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/jobPreview/:analysisId',
        name: 'Teacher/JobPreview',
        component: () => import(/* webpackChunkName: "JobPreview" */ '@/views/Teacher/caseAnalysis/jobPreview.vue'),
        /** view : preview,see,submit */
        meta: {
          title: '作业预览', // 无数据预览
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
      {
        path: '/Teacher/analysisAnswer',
        name: 'Teacher/analysisAnswer',
        component: () => import(/* webpackChunkName: "AnalysisAnswer" */ '@/views/Teacher/caseAnalysis/analysisAnswer.vue'),
        /** view : preview,see,submit */
        meta: {
          title: '作业预览', // 无数据预览
          icon: '',
          role: ['admin', 'teacher'],
        },
        /**
         * 案例分析-分析页面——全局查询锚点
         * view: 'submit', // preview,see,submit —— required
         * jobId: record.assignmentId, // jobId这是教师端的作业id —— required
         * assignmentId: record.id, // 这是分配表的id —— 查询学生做的纪录需要
         * assignmentModel: record.assignmentModel, // 分配模式 —— 学生做题需要
         * stuCode: record.userId, // 查询指定学生的code —— 只有教师端批改才需要
         */
      },
      {
        path: '/Teacher/userInfo',
        name: 'Teacher/UserInfo',
        component: () => import(/* webpackChunkName: "UserInfo" */ '../views/Public/UserInfo.vue'),
        meta: {
          title: '个人中心',
          icon: '',
          role: ['admin', 'teacher', 'student'],
        },
      },
      {
        path: '/Teacher/addJob/:jobId?',
        name: 'Teacher/AddJob',
        component: () => import(/* webpackChunkName: "AddJob" */ '@/views/Teacher/addJob/index.vue'),
        meta: {
          title: '添加作业',
          icon: '',
          role: ['admin', 'teacher'],
        },
      },
    ],
  },
  ...StudentRouter,
];

const router = createRouter({
  history: createWebHashHistory('/hero'),
  routes,
});

const whiteList = ['/', '/404']; // 路由白名单：不需要登录也可以进入
router.beforeEach((to, from, next) => {
  // const { role, code } = commonStore.getState();
  const { role, code } = getUserInfo();
  if (to.path === '/') {
    commonStore.reset();
  }
  if (whiteList.includes(to.path)) {
    // next(); // 判断白名单
    console.log(to.name, 'to.name');
    if (role && code && to.name === 'login') {
      const path = role === 'student' ? '/Student/home' : '/Teacher/home';
      console.log(111, role, path);
      next({ path }); // 判断是否已经登录过
    } else {
      next(); // 判断白名单
    }
  } else if (!role || !code) {
    next({ path: '/' }); // 判断登录
  } else if (to.meta.role && (to.meta.role as string).includes(role)) {
    next(); // 判断页面、角色权限
  } else {
    next({ path: '/404' });
  }
});

export default router;
